import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Pagination, VehicleContainerModel, VehicleService } from 'blg-akaun-ts-lib';
import { Observable, combineLatest, forkJoin, iif, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, scan, startWith, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { AppConfig } from '../../visa';
import { IDropdown } from '../../models/search-model';

@Component({
  selector: 'app-vehicle-brand-name-dropdown',
  templateUrl: './vehicle-brand-name-dropdown.component.html',
  styleUrls: ['./vehicle-brand-name-dropdown.component.css']
})
export class VehicleBrandNameDropdownComponent implements OnInit, OnDestroy {
  @Input() control!: FormControl;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() fieldLabel = 'Vehicle Brand Name';
  @Output() selectionChange = new EventEmitter();

  private subs = new SubSink();
  apiVisa = AppConfig.apiVisa;
  filterControl = new FormControl();
  formValue$: Observable<string>;
  initialValue$: Observable<IDropdown[]>;
  both$: Observable<IDropdown[]>;
  computedInitDropdown$: Observable<IDropdown[]>;
  finalDropdown$: Observable<IDropdown[]>;

  initialDropdown$ = this.vehicleService.getByCriteria(this.getBasePagination(), this.apiVisa).pipe(
    map(resp => resp?.data?.map(this.mapContainerToDropDown)),
    catchError(err => {
      console.error(err);
      return of([] as IDropdown[]);
    })
  );

  constructor(private vehicleService: VehicleService) {}

  ngOnInit() {
    this.initialValue$ = this.vehicleService.getByCriteria(this.getInitPagination(), this.apiVisa).pipe(
      map(resp => resp?.data?.map(this.mapContainerToDropDown)),
      catchError(err => {
        console.error(err);
        return of([] as IDropdown[]);
      })
    );

    this.both$ = forkJoin([this.initialDropdown$, this.initialValue$]).pipe(
      map(([a, b]) => [...a, ...b])
    );

    this.computedInitDropdown$ = iif(() => this.control.value === null, this.initialDropdown$, this.both$);

    this.formValue$ = this.filterControl.valueChanges.pipe(
      startWith(<string>null),
      debounceTime(300),
      distinctUntilChanged()
    );

    this.finalDropdown$ = combineLatest([this.computedInitDropdown$, this.formValue$]).pipe(
      switchMap(([vehicleList, search]) => {
        if (!search) return of({ og: vehicleList, filt: null, new: false });

        const filter = vehicleList.filter(c => c.viewValue.toLowerCase().includes(search.toLowerCase()));

        if (filter.length) return of({ og: vehicleList, filt: filter, new: false });

        const pag = this.getBasePagination();
        pag.conditionalCriteria.push({ columnName: 'brand_name', operator: 'LIKE', value: `%${search}%` });

        return this.vehicleService.getByCriteria(pag, this.apiVisa).pipe(
          map(resp => resp?.data?.map(this.mapContainerToDropDown)),
          map(options => ({ og: vehicleList, filt: options, new: true })),
          catchError(err => {
            console.error(err);
            return of({ og: vehicleList, filt: [] as IDropdown[], new: false });
          })
        );
      }),
      scan((acc, cur) => {
        const initial = acc.acc.length ? acc.acc : cur.og;
        const combinedList = cur.new ? [...initial, ...cur.filt] : [...initial];

        // Ensure the dropdown options are unique
        const uniqueOptions = combinedList.reduce((unique: IDropdown[], item) => {
          if (!unique.some(u => u.value === item.value)) {
            unique.push(item);
          }
          return unique;
        }, []);

        return {
          acc: uniqueOptions,
          final: cur.filt ? cur.filt : uniqueOptions
        };
      }, { acc: [], final: [] } as { acc: IDropdown[], final: IDropdown[] }),
      map(acc => acc.final)
    );
  }

  getBasePagination() {
    const pagination = new Pagination(0, 30);
    pagination.conditionalCriteria = [
      { columnName: "orderBy", operator: "=", value: "brand_name" },
      { columnName: "order", operator: "=", value: "ASC" },
      { columnName: "status", operator: "=", value: "ACTIVE" }
    ];
    return pagination;
  }

  getInitPagination() {
    const pagination = this.getBasePagination();
    pagination.conditionalCriteria = this.control?.value
      ? [...pagination.conditionalCriteria, {
        columnName: 'brand_name', operator: '=', value: this.control?.value
      }]
      : [...pagination.conditionalCriteria];
    return pagination;
  }

  mapContainerToDropDown = (container: VehicleContainerModel) => ({
    viewValue: container.bl_fi_mst_entity_vehicle_hdr.brand_name?.toString(),
    value: container.bl_fi_mst_entity_vehicle_hdr.brand_name?.toString()
  } as IDropdown);

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
